import Axios, { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { caseKeys, scenarioKeys } from "src/query-keys";
import { Scenario, APIError } from "src/types";

export type ScenarioMutationData = Pick<Scenario, "name">;
export type ScenarioCopyMutationData = { scenarioName: string };

// TODO: rename file to useScenarioMutations
export function useScenarioCreateMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(caseId: number | string, data: ScenarioMutationData) {
    const response = await Axios.post(`/planning-api/cases/${caseId}/scenarios`, data);
    return response.data;
  }

  return useMutation((values: ScenarioMutationData) => mutationFn(caseId, values), {
    onSettled: () => queryClient.invalidateQueries(scenarioKeys.all(caseId)),
  });
}

export function useScenarioUpdateMutation(caseId: number | string, scenarioId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(scenarioId: number | string, data: ScenarioMutationData): Promise<void> {
    return Axios.put(`/planning-api/scenarios/${scenarioId}`, data);
  }

  return useMutation<void, AxiosError<APIError[]>, Scenario, unknown>(
    (values: ScenarioMutationData) => mutationFn(scenarioId, values),
    {
      onMutate: async (updatedScenario: Scenario) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(scenarioKeys.all(caseId));

        // Optimistically update to the new value
        queryClient.setQueryData(scenarioKeys.detail(scenarioId), () => updatedScenario);
        queryClient.setQueryData<Scenario[]>(scenarioKeys.all(caseId), (oldData) => {
          if (!oldData) return [];
          return [...oldData].map((item) => {
            if (item.id === updatedScenario.id) return updatedScenario;
            return item;
          });
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(scenarioKeys.all(caseId));
      },
    }
  );
}

export function useScenarioCopyMutation(scenarioId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(scenarioId: number | string, data: ScenarioCopyMutationData): Promise<void> {
    return Axios.post(`/planning-api/scenarios/${scenarioId}/copy`, data);
  }

  return useMutation<void, AxiosError<APIError[]>, ScenarioCopyMutationData, unknown>(
    (values: ScenarioCopyMutationData) => mutationFn(scenarioId, values),
    {
      onSettled: () => queryClient.invalidateQueries(caseKeys.all),
    }
  );
}

export function useScenarioDeleteMutation(scenarioId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(scenarioId: number | string) {
    return Axios.delete(`/planning-api/scenarios/${scenarioId}`);
  }

  return useMutation(() => mutationFn(scenarioId), {
    onSettled: () => queryClient.invalidateQueries(caseKeys.all),
  });
}

export function useVehicleTypes(scenarioId: number | string) {
  async function fetchVehicleTypes(scenarioId: number | string): Promise<string[]> {
    const response = await Axios.get(`/planning-api/scenarios/${scenarioId}/vehicletypes`);
    return response.data;
  }
  return useQuery<string[], Error>(scenarioKeys.vehicleTypes(scenarioId), () => fetchVehicleTypes(scenarioId));
}
